import { Check, FileText } from "lucide-react";
import CTAButton from "@/components/CTAButton";
import { API } from "@/lib/api";
import {
  CONVENTIONAL_DD, PFAS_LAYER, DELIVERABLES, HOW_IT_WORKS, SAMPLE_CARDS,
} from "@/lib/constants";

const Wrap = ({ id, children, className = "" }) => (
  <section id={id} className={`border-b border-white/[0.08] ${className}`}>
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">{children}</div>
  </section>
);

export function RiskScreenExplainer() {
  return (
    <Wrap id="risk-screen-explained">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-6">
          <div className="kicker mb-4">PFAS Brownfield Risk Screen · From £1,950 + VAT · 5–7 working days</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight" data-testid="screen-explainer-title">
            A PFAS-specific desk-based intelligence review for a defined site.
          </h2>
          <p className="mt-6 text-white/65 leading-relaxed">
            We assess potential PFAS source activities, historic land use, off-site influences, relevant pathways, available public evidence and key information gaps.
          </p>
          <p className="mt-4 text-white/65 leading-relaxed">
            You receive a clear Red / Amber / Green decision view with recommended next steps.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <CTAButton to="/risk-screen" primary testId="explainer-cta-risk-screen">Request a PFAS Brownfield Risk Screen</CTAButton>
          </div>
          <p className="mt-6 text-xs text-white/40 max-w-lg leading-relaxed">
            The screen does not confirm PFAS contamination, confirm a site is clean, determine legal liability, replace sampling, or guarantee planning or regulatory outcomes.
          </p>
        </div>
        <div className="lg:col-span-6">
          <div className="kicker mb-4">PFAS-relevant source activity review</div>
          <p className="text-white/65 leading-relaxed">
            Depending on the site and available evidence, this may include AFFF use, fire-training activity, airports and airfields, firefighting facilities, selected industrial processes, wastewater or drainage pathways, neighbouring historic uses and other evidence-supported PFAS source activities.
          </p>
          <div className="mt-6 border border-white/10 bg-[#0C0E13] p-5">
            <div className="kicker text-emerald-400">Evidence-weighted</div>
            <p className="mt-2 text-sm text-white/65 leading-relaxed">
              More specific source evidence carries greater significance than broad historic land-use labels. A generic industrial estate or landfill is not automatically treated as equivalent to a known AFFF or fire-training source.
            </p>
          </div>
        </div>
      </div>
    </Wrap>
  );
}

export function SampleOutputs({ visible }) {
  if (!visible) return null;
  return (
    <Wrap id="sample-outputs">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="max-w-3xl">
          <div className="kicker mb-4">Demonstration sample · Fictional site</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight" data-testid="sample-outputs-title">
            See what a PFAS Brownfield Risk Screen looks like
          </h2>
          <p className="mt-6 text-white/65 leading-relaxed">
            Understand the type of analysis, evidence and decision-support included before commissioning a review.
          </p>
        </div>
        <CTAButton href={`${API}/samples/risk-screen.pdf`} primary testId="sample-cta-view">
          <FileText className="w-3.5 h-3.5" /> View Sample PFAS Risk Screen
        </CTAButton>
      </div>
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.08]">
        {SAMPLE_CARDS.map((c, i) => (
          <div key={c.t} className="bg-[#0A0B0E] p-6 hover-lift hover:bg-[#12141A]" data-testid={`sample-card-${i}`}>
            <div className="text-[10px] font-mono-alt tracking-[0.18em] uppercase text-white/40">Sample · 0{i + 1}</div>
            <div className="mt-3 font-display text-xl leading-snug">{c.t}</div>
            <p className="mt-2 text-sm text-white/55 leading-relaxed">{c.d}</p>
          </div>
        ))}
      </div>
      <p className="mt-6 text-xs text-white/40 max-w-3xl leading-relaxed">
        The sample is a demonstration based on a fictional site and fictional evidence. It is not a client report and must not be relied upon for any decision.
      </p>
    </Wrap>
  );
}

export function Complement() {
  return (
    <Wrap id="complements">
      <div className="kicker mb-4">Complements, not replaces</div>
      <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-4xl" data-testid="complement-title">
        How the PFAS Risk Screen complements conventional contaminated-land due diligence
      </h2>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.06] border border-white/[0.08]">
        <ColumnList kicker="Conventional contaminated-land due diligence" items={CONVENTIONAL_DD} tone="text-white/60" />
        <ColumnList kicker="PFAS Risk Intelligence layer" items={PFAS_LAYER} tone="text-emerald-400" strong />
      </div>
      <p className="mt-8 text-sm text-white/55 max-w-3xl leading-relaxed">
        The PFAS Brownfield Risk Screen is designed to complement, not replace, Phase 1 desk studies, contaminated-land consultants, laboratory testing or regulatory assessment.
      </p>
    </Wrap>
  );
}

function ColumnList({ kicker, items, tone, strong }) {
  return (
    <div className="bg-[#0A0B0E] p-6 md:p-8">
      <div className={`kicker ${tone}`}>{kicker}</div>
      <ul className="mt-6 space-y-3">
        {items.map((it) => (
          <li key={it} className="flex gap-3 text-sm leading-relaxed">
            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${strong ? "text-emerald-400" : "text-white/40"}`} strokeWidth={2.5} />
            <span className={strong ? "text-white/85" : "text-white/65"}>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function HowItWorks() {
  return (
    <Wrap id="how-it-works">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="kicker mb-4">How the Risk Screen works</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight">From brief to decision view in 5–7 working days.</h2>
        </div>
        <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/[0.06] border border-white/[0.08]">
          {HOW_IT_WORKS.map((s) => (
            <div key={s.n} className="bg-[#0A0B0E] p-6">
              <div className="text-[11px] font-mono-alt tracking-[0.18em] text-emerald-400">{s.n}</div>
              <div className="mt-3 font-display text-xl">{s.t}</div>
              <p className="mt-2 text-sm text-white/55 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </Wrap>
  );
}

export function Deliverables() {
  return (
    <Wrap id="deliverables">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="kicker mb-4">What the client receives</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight">A decision-focused report, not a data dump.</h2>
          <p className="mt-6 text-white/60 text-sm leading-relaxed">
            Every screen closes with scope and limitations so the preliminary view is read in context.
          </p>
        </div>
        <div className="lg:col-span-8">
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3" data-testid="deliverables-list">
            {DELIVERABLES.map((d, i) => (
              <li key={d} className="flex gap-3 text-sm border-b border-white/[0.06] py-2">
                <span className="text-[10px] font-mono-alt tracking-[0.14em] text-white/35 w-6 shrink-0 pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-white/80">{d}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Wrap>
  );
}
